// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Header, SpaceBetween } from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import { ArcRowsTable, useArcRowsEditor } from '../../Common/ArcRows';
import DetailsPanel from '../../Common/DetailsPanel';
import { EditActions, RowEditorAlerts } from '../../Common/RowEditorControls';
import VersionHeader from '../../Common/VersionHeader';
import { useVersionedRecord, VersionedRecordPage } from '../../Common/VersionedRecordPage';
import { byArc } from '../../../utils/arcRows';
import { formatLocale, Locale, useLocales } from '../../../utils/locales';
import { valueOrDash } from '../../../utils/ReadableConverter';
import { ProposedChangeSet } from '../ChangeSets/changeSets';
import { DefaultTerField } from './MetadataFields';
import * as terGroups from './terGroups';

interface TerGroupEditorProps {
  group: terGroups.TerGroup;
  // Null until the locales have loaded; editing needs them.
  locales: Locale[] | null;
  canWrite: boolean;
  // Rejects with the API error when the save fails.
  onSave: (request: terGroups.TerGroupUpdateRequest) => Promise<unknown>;
  onReload: () => void;
}

/** The group's details and TERs, edited together and saved as one new version. */
const TerGroupEditor = ({
  group,
  locales,
  canWrite,
  onSave,
  onReload,
}: TerGroupEditorProps): JSX.Element => {
  const [metadata, setMetadata] = useState<terGroups.Metadata>(() => terGroups.metadataOf(group));
  // Memoized: the editor starts over whenever the list it is given changes.
  const ters = useMemo(
    () => [...group.ters].sort((a, b) => byArc(a.languagePair, b.languagePair)),
    [group.ters]
  );
  const editor = useArcRowsEditor(terGroups.terRows, ters, locales, {
    emptyMessage: 'Add at least one TER.',
    onSave: ters => onSave(terGroups.toUpdateRequest(group.version, metadata, ters)),
  });

  useEffect(() => setMetadata(terGroups.metadataOf(group)), [group]);

  const changed =
    terGroups.terRows.hasChanges(editor.rows, group.ters.length) ||
    terGroups.defaultTerChanged(metadata, group);

  const viewOrEdit = (value: React.ReactNode, input: React.ReactNode): React.ReactNode =>
    editor.editing ? input : value;

  return (
    <SpaceBetween size="m">
      <RowEditorAlerts
        editor={editor}
        idPrefix="terGroup"
        conflictHeader={terGroups.CONFLICT_HEADER}
        onReload={onReload}
      />
      <VersionHeader
        title={group.groupName}
        record={group}
        actions={
          canWrite ? (
            <EditActions
              editor={editor}
              ids={{
                edit: 'editTerGroup',
                add: 'addTer',
                cancel: 'cancelTerGroup',
                save: 'saveTerGroup',
              }}
              ready={locales !== null}
              changed={changed && terGroups.defaultTersValid(metadata)}
              onCancel={(): void => setMetadata(terGroups.metadataOf(group))}
            />
          ) : (
            undefined
          )
        }
      />
      {terGroups.lacksDefaultTers(group) && (
        <Alert id="terGroupDefaultTersMissing" type="warning">
          {terGroups.missingDefaultTersMessage(group)}
        </Alert>
      )}
      <DetailsPanel
        items={[
          { label: 'Group name', value: <span id="groupNameValue">{group.groupName}</span> },
          // Fixed at create, like the name, so shown as values in edit mode too.
          ...terGroups.METADATA_FIELDS.map(({ field, label }) => ({
            label,
            value: <span id={`${field}Value`}>{valueOrDash(group[field])}</span>,
          })),
          ...terGroups.DEFAULT_TERS.map(({ field, label }) => ({
            label,
            value: viewOrEdit(
              <span id={`${field}Value`}>{valueOrDash(metadata[field])}</span>,
              <DefaultTerField
                field={field}
                values={metadata}
                disabled={editor.submitting}
                onChange={setMetadata}
              />
            ),
          })),
        ]}
      />
      <ArcRowsTable
        id="tersTable"
        list={terGroups.terRows}
        editor={editor}
        locales={locales}
        display={formatLocale}
        rowErrorId="terRowError"
        removeId="removeTer"
        header={<Header variant="h3">{`TERs (${editor.rows.length})`}</Header>}
        empty="No TERs. Add a row to create one."
      />
    </SpaceBetween>
  );
};

interface TerGroupDetailProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPut: (path: string, body: object) => Promise<unknown>;
  };
  terGroup: string;
  canWrite: boolean;
}

export const TerGroupDetail = ({
  apiClient,
  terGroup,
  canWrite,
}: TerGroupDetailProps): JSX.Element => {
  const fetchGroup = useCallback(
    () => apiClient.httpGet(terGroups.terGroupPath(terGroup)) as Promise<terGroups.TerGroup>,
    [apiClient, terGroup]
  );
  const page = useVersionedRecord(fetchGroup, terGroups.describeError);
  const locales = useLocales(apiClient, terGroups.describeError);

  const save = (request: terGroups.TerGroupUpdateRequest): Promise<void> =>
    page.save(
      () =>
        apiClient.httpPut(terGroups.terGroupPath(terGroup), request) as Promise<
          terGroups.TerGroup | ProposedChangeSet
        >
    );

  return (
    <VersionedRecordPage
      idPrefix="terGroup"
      page={page}
      locales={locales}
      loadingText="Loading TER group"
      loadErrorHeader="Could not load the TER group"
      retryId="retryTerGroup"
    >
      {page.record && (
        <TerGroupEditor
          group={page.record}
          locales={locales.locales}
          canWrite={canWrite}
          onSave={save}
          onReload={page.reload}
        />
      )}
    </VersionedRecordPage>
  );
};

TerGroupDetail.propTypes = {
  apiClient: PropTypes.object.isRequired,
  terGroup: PropTypes.string.isRequired,
  canWrite: PropTypes.bool.isRequired,
};

const TerGroupDetailView = (props: Omit<TerGroupDetailProps, 'apiClient'>): JSX.Element => (
  <TerGroupDetail apiClient={ApiClient} {...props} />
);

export default TerGroupDetailView;
